import { captureEvent } from "~/helpers/capture_posthog_event";

const percentile = (sortedValues, p) => {
  if (sortedValues.length === 0) {
    return null;
  }
  // Nearest-rank: rank = ceil(p/100 * N), then 0-indexed
  const rank = Math.ceil((p / 100) * sortedValues.length);
  const index = Math.max(0, Math.min(sortedValues.length - 1, rank - 1));
  return sortedValues[index];
};

const mean = (values) => {
  if (values.length === 0) {
    return null;
  }
  return values.reduce((acc, v) => acc + v, 0) / values.length;
};

const min = (values) => {
  if (values.length === 0) {
    return null;
  }
  return values.reduce((m, v) => (v < m ? v : m), Infinity);
};

const collectValid = (samples, key) =>
  samples
    .map((s) => s[key])
    .filter((v) => typeof v === "number" && !Number.isNaN(v));

const whitelistConstraints = (settings) => {
  if (!settings || typeof settings !== "object") {
    return null;
  }
  const {
    echoCancellation,
    noiseSuppression,
    autoGainControl,
    sampleRate,
    channelCount,
  } = settings;
  return {
    echoCancellation:
      typeof echoCancellation === "boolean" ? echoCancellation : null,
    noiseSuppression:
      typeof noiseSuppression === "boolean" ? noiseSuppression : null,
    autoGainControl:
      typeof autoGainControl === "boolean" ? autoGainControl : null,
    sampleRate: Number.isFinite(sampleRate) ? sampleRate : null,
    channelCount: Number.isFinite(channelCount) ? channelCount : null,
  };
};

const parseBrowser = (ua) => {
  if (!ua) {
    return { name: null, version: null };
  }

  if (/Edg\//.test(ua)) {
    return {
      name: "edge",
      version: (ua.match(/Edg\/([\d.]+)/) || [])[1] || null,
    };
  }
  if (/Firefox\//.test(ua)) {
    return {
      name: "firefox",
      version: (ua.match(/Firefox\/([\d.]+)/) || [])[1] || null,
    };
  }
  if (/Chrome\//.test(ua)) {
    return {
      name: "chrome",
      version: (ua.match(/Chrome\/([\d.]+)/) || [])[1] || null,
    };
  }
  if (/Safari\//.test(ua)) {
    return {
      name: "safari",
      version: (ua.match(/Version\/([\d.]+)/) || [])[1] || null,
    };
  }
  return { name: "other", version: null };
};

const getCallStatus = (call) => {
  if (!call || typeof call.status !== "function") {
    return null;
  }

  try {
    return call.status();
  } catch (e) {
    return null;
  }
};

const sanitizeTwilioError = (error) => {
  if (!error) {
    return { code: null, message: null };
  }

  return {
    code: error.code || null,
    message: error.message || null,
  };
};

export const createCallQualityCollector = (call, callbacks = {}) => {
  const samples = [];
  const warnings = new Set();
  const activeWarnings = new Set();
  const clearedWarnings = new Set();
  let latestSdkError = null;
  let reconnectCount = 0;
  let reconnectedCount = 0;
  let disconnectCause = null;
  const startedAt = Date.now();

  // Stable handler refs so attach/detach can pair them.
  const onSample = (sample) => samples.push(sample);
  const onWarning = (name, data) => {
    warnings.add(name);
    activeWarnings.add(name);
    callbacks.onWarning?.(name, data);
  };
  const onWarningCleared = (name) => {
    activeWarnings.delete(name);
    clearedWarnings.add(name);
    callbacks.onWarningCleared?.(name, Array.from(activeWarnings));
  };
  const onReconnecting = (error) => {
    reconnectCount += 1;
    if (error) {
      latestSdkError = sanitizeTwilioError(error);
    }
    callbacks.onReconnecting?.(error);
  };
  const onReconnected = () => {
    reconnectedCount += 1;
    callbacks.onReconnected?.();
  };

  return {
    attach() {
      if (!call || typeof call.on !== "function") {
        return;
      }
      call.on("sample", onSample);
      call.on("warning", onWarning);
      call.on("warning-cleared", onWarningCleared);
      call.on("reconnecting", onReconnecting);
      call.on("reconnected", onReconnected);
    },

    detach() {
      if (!call || typeof call.off !== "function") {
        return;
      }
      try {
        call.off("sample", onSample);
        call.off("warning", onWarning);
        call.off("warning-cleared", onWarningCleared);
        call.off("reconnecting", onReconnecting);
        call.off("reconnected", onReconnected);
      } catch (e) {
        /* SDK may not support off; ignore */
      }
    },

    setDisconnectCause(cause) {
      disconnectCause = cause;
    },

    setSdkError(error) {
      latestSdkError = sanitizeTwilioError(error);
    },

    emit(extra = {}) {
      const mosValues = collectValid(samples, "mos").sort((a, b) => a - b);
      const jitterValues = collectValid(samples, "jitter").sort(
        (a, b) => a - b,
      );
      const rttValues = collectValid(samples, "rtt").sort((a, b) => a - b);
      const inputLevels = collectValid(samples, "audioInputLevel");
      const outputLevels = collectValid(samples, "audioOutputLevel");

      const firstSample = samples[0];
      const lastSample = samples[samples.length - 1];

      const codecStart = firstSample ? firstSample.codecName : null;
      const codecEnd = lastSample ? lastSample.codecName : null;

      const browser = parseBrowser(
        typeof navigator !== "undefined" ? navigator.userAgent : "",
      );

      captureEvent("call_ended", {
        call_sid:
          extra.callSid || call?.parameters?.CallSid || null,
        dial_attempt_id: extra.dialAttemptId || null,
        direction: extra.direction || null,
        call_status_at_event: extra.callStatus || getCallStatus(call),
        answered: extra.answered ?? null,
        activity_creation_attempted: extra.activityCreationAttempted ?? null,
        codec_start: codecStart,
        codec_end: codecEnd,
        codec_changed: codecStart && codecEnd ? codecStart !== codecEnd : false,
        mos_avg: mean(mosValues),
        mos_min: mosValues.length > 0 ? mosValues[0] : null,
        mos_p95: percentile(mosValues, 95),
        jitter_avg: mean(jitterValues),
        jitter_p95: percentile(jitterValues, 95),
        rtt_avg: mean(rttValues),
        rtt_p95: percentile(rttValues, 95),
        packets_lost_total:
          lastSample && typeof lastSample.packetsLost === "number"
            ? lastSample.packetsLost
            : null,
        audio_input_level_min: min(inputLevels),
        audio_input_level_avg: mean(inputLevels),
        audio_output_level_avg: mean(outputLevels),
        warnings: Array.from(warnings),
        active_warnings: Array.from(activeWarnings),
        cleared_warnings: Array.from(clearedWarnings),
        reconnect_count: reconnectCount,
        reconnected_count: reconnectedCount,
        sdk_error_code: latestSdkError?.code || null,
        sdk_error_message: latestSdkError?.message || null,
        duration_ms: Date.now() - startedAt,
        disconnect_cause: disconnectCause,
        mic_label: extra.micLabel || null,
        speaker_label: extra.speakerLabel || null,
        effective_constraints: whitelistConstraints(extra.effectiveConstraints),
        requested_edge: extra.requestedEdge || null,
        actual_edge: extra.actualEdge || null,
        connection_type:
          typeof navigator !== "undefined" && navigator.connection
            ? navigator.connection.effectiveType
            : null,
        browser: browser.name,
        browser_version: browser.version,
      });
    },
  };
};
